import { useMemo, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { BUSINESS } from "@/lib/site";
import type { NewsletterApiResponse } from "@/lib/newsletter-schema";

/**
 * Newsletter signup. Posts to the site API, which emails the park inbox
 * (amigoinn13@gmail.com). No fake success states.
 */
const ENDPOINT =
  (import.meta.env["VITE_NEWSLETTER_ENDPOINT"] as string | undefined)?.trim() || "/api/newsletter";

type Status = "idle" | "submitting" | "success" | "error";

type NewsletterFormProps = {
  heading?: string;
  description?: string;
};

export function NewsletterForm({
  heading = "Park News",
  description = "Seasonal weekends, events and park updates.",
}: NewsletterFormProps) {
  const startedAt = useMemo(() => Date.now(), []);
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("submitting");
    setMessage("");
    try {
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          website,
          formStartedAt: startedAt,
        }),
      });
      const data = (await res.json().catch(() => null)) as NewsletterApiResponse | null;
      if (!res.ok || !data?.ok) {
        throw new Error(data && !data.ok ? data.message : "Request failed");
      }
      setStatus("success");
      setMessage(data.message || "Thanks — you're on the list.");
      setEmail("");
    } catch (error) {
      setStatus("error");
      setMessage(
        error instanceof Error && error.message
          ? error.message
          : `Something went wrong. Please try again or email us at ${BUSINESS.email}.`,
      );
    }
  }

  return (
    <form onSubmit={onSubmit} className="relative max-w-sm" noValidate>
      <label htmlFor="newsletter-email" className="eyebrow text-sand">
        {heading}
      </label>
      <p className="mt-2 text-sm leading-relaxed text-cream/70">{description}</p>

      <div
        className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden"
        aria-hidden="true"
      >
        <label htmlFor="newsletter-website">Website</label>
        <input
          id="newsletter-website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      <div className="mt-4 flex gap-2">
        <input
          id="newsletter-email"
          name="email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          aria-describedby="newsletter-status"
          className="h-11 min-w-0 flex-1 rounded-sm border border-cream/25 bg-cream/10 px-3 text-sm text-cream placeholder:text-cream/50"
        />
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex h-11 min-w-11 items-center justify-center gap-2 rounded-sm bg-clay px-4 text-xs font-semibold uppercase tracking-wide text-accent-foreground transition-[filter] hover:brightness-110 disabled:opacity-60"
        >
          {status === "submitting" ? (
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          ) : (
            <Send className="size-4" aria-hidden="true" />
          )}
          <span className="sr-only sm:not-sr-only">Sign Up</span>
        </button>
      </div>

      <p
        id="newsletter-status"
        role="status"
        aria-live="polite"
        className={
          status === "error"
            ? "mt-3 text-sm font-medium text-sand"
            : "mt-3 text-sm font-medium text-cream/90"
        }
      >
        {message}
      </p>
    </form>
  );
}
